import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    TopToolbar,
    SelectInput,
    Button,
    FunctionField,
    useDataProvider,
    useListContext,
    useNotify,
    ShowButton,
    useListFilterContext,
    Filter,
    DateField
} from "react-admin";


// 创建自定义过滤器组件
const OtaUpgradeFilter = ({ deviceTypes, customerTypeChoices }) => {
    const { filterValues } = useListFilterContext();
    const dataProvider = useDataProvider();
    const [selectedDeviceType, setSelectedDeviceType] = useState<string | null>(
        filterValues?.deviceType || null
    );
    const [selectedOtaType, setSelectedOtaType] = useState<string | null>(
        filterValues?.otaType || null
    );
    const [otaPackage, setOtaPackage] = useState([]);
    const [otaPackage2, setOtaPackage2] = useState([]);

    useEffect(() => {
        setSelectedDeviceType(filterValues?.deviceType || null);
        setSelectedOtaType(filterValues?.otaType || null);
    }, [filterValues]);

    useEffect(() => {
        const fetchOtaPackage = async () => {
            try {
                if (selectedDeviceType && selectedOtaType) {
                    let types = await dataProvider.otaPackage({
                        deviceType: selectedDeviceType,
                        otaType: selectedOtaType
                    });

                    let data1 = types.map((item) => ({
                        id: item.version,
                        name: item.version
                    }));

                    let data2 = types.map((item) => ({
                        id: item.id,
                        name: item.version + '(' + item.title + ')'
                    }));

                    setOtaPackage2(data2);
                    setOtaPackage(data1);
                } else {
                    setOtaPackage2([]);
                    setOtaPackage([]);
                }
            } catch (error) {
                console.error("获取设备类型失败:", error);
                setOtaPackage2([]);
                setOtaPackage([]);
            }
        };

        fetchOtaPackage();
    }, [selectedDeviceType, selectedOtaType]);

    return (
        <Filter>
            <SelectInput
                alwaysOn
                key="deviceType"
                source="deviceType"
                label="设备类型"
                choices={deviceTypes}
            />
            <SelectInput
                alwaysOn
                key="otaType"
                source="otaType"
                label="OTA类型"
                choices={[
                    { id: 'FIRMWARE', name: '固件' },
                    { id: 'SOFTWARE', name: '软件' }
                ]}
            />
            <SelectInput
                alwaysOn
                key="currentVersion"
                source="currentVersion"
                label="当前设备版本"
                choices={otaPackage}
                disabled={!selectedDeviceType || !selectedOtaType}
            />
            <SelectInput
                alwaysOn
                key="otaPackageId"
                source="otaPackageId"
                label="可升级版本"
                choices={otaPackage2}
                disabled={!selectedDeviceType || !selectedOtaType}
            />
            <TextInput
                alwaysOn
                key="sns"
                source="sns"
                label="设备SN"
            />
            <SelectInput
                alwaysOn
                key="saleCustomerIds"
                source="saleCustomerIds"
                label="设备商家"
                choices={customerTypeChoices}
            />
        </Filter>
    );
};


const ListActions = () => {
    const { setFilters, filterValues, total } = useListContext();  // 获取列表上下文
    const hasData = total > 0; // 判断是否有数据

    const initialFilters = {};  // 初始过滤器值
    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法

    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const handleBatchUpgrade = () => {
        if (window.confirm(`确定对设备执行升级操作？`)) {
            const formData = {
                ...filterValues,
                sns: filterValues.sns.split(/[,，]+/)
                    .map(sn => sn.trim())
                    .filter(sn => sn.length > 0),
            };
            dataProvider.startOtaUpgrade(formData)
                .then(() => {
                    notify('批量升级成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '批量升级失败', { type: 'error' });
                });
        }
    }

    const exportDetails = () => {
        if (window.confirm(`确认导出设备OTA列表？`)) {
            const formData = {
                ...filterValues,
            };
            dataProvider.deviceOtaExport(formData)
                .then(() => {
                    notify('导出设备OTA列表成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '导出设备OTA列表失败', { type: 'error' });
                })
        }
    }
    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={Object.keys(filterValues).length === 0}  // 当没有过滤条件时禁用
            />
            <Button
                label="批量升级"
                disabled={!hasData}
                onClick={handleBatchUpgrade}
            />
            <Button
                label="导出"
                onClick={exportDetails}
            />
        </TopToolbar>
    );
};

// 统一按钮样式
const tableButtonStyle = {
    whiteSpace: 'nowrap',
    color: '#1976d2',
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04)'
    }
};

export const otaUpgradeList = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [customerTypeChoices, setCustomerTypeChoices] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const notify = useNotify(); // 获取通知方法

    useEffect(() => {
        const fetchCustomerTypes = async () => {
            try {
                const types = await dataProvider.getSalesCustomers();
                setCustomerTypeChoices(types);
            } catch (error) {
                console.error("获取客户类型失败:", error);
            }
        };

        const fetchDeviceTypes = async () => {
            try {
                let types = await dataProvider.getDeviceList();
                types = types.map((item) => ({
                    id: item.name,
                    name: item.name
                }));

                setDeviceTypes(types);
            } catch (error) {
                console.error("获取设备类型失败:", error);
            }
        };
        fetchDeviceTypes();
        fetchCustomerTypes();
    }, []);

    const OperationButton = ({ record }) => {
        const { filterValues } = useListContext(); // ✅ 现在可以正确获取上下文

        const handleUpgrade = (e) => {
            e.stopPropagation(); // 阻止事件冒泡
            if (window.confirm(`确定对设备 ${record.sn} 执行升级操作？`)) {
                const formData = {
                    ...filterValues,
                    sns: [record.sn]
                };

                dataProvider.startOtaUpgrade(formData)
                    .then(() => notify('设备升级中,请稍后', { type: 'success' }))
                    .catch(error => notify(error.message || '升级失败', { type: 'error' }));
            }
        };

        return (
            <Button
                label="升级"
                onClick={handleUpgrade}
                sx={tableButtonStyle}
            />
        );
    };

    return (
        <List filters={<OtaUpgradeFilter deviceTypes={deviceTypes} customerTypeChoices={customerTypeChoices} />} actions={<ListActions />} empty={false}>
            <div style={{ margin: '20px 10px' }}>提示:请先选择设备类型，OTA类型，再选择现在版本，以及您要升级的版本，再进行升级</div>
            <Datagrid bulkActionButtons={false} rowClick={false}>
                {/* 修正字段映射 */}
                <TextField source="id" label="序号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="deviceType" label="设备类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="sn" label="设备SN" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="saleCustomerName" label="设备商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="trafficCustomerName" label="流量商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <DateField source="activateTime" label="用户绑定日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="在线时长"
                    source="totalOnlineDuration"
                    render={record => {
                        const totalSeconds = record.totalOnlineDuration;
                        const days = Math.floor(totalSeconds / 86400);
                        const hours = Math.floor((totalSeconds % 86400) / 3600);

                        if (days > 0) {
                            return `${days}天${hours}小时`;
                        } else {
                            return `${hours}小时`;
                        }
                    }}
                    sortable={false}
                />
                <FunctionField
                    label="设备位置"
                    source="city"
                    render={record =>
                        (record.province || '').replace(/省$/, '') +
                        ' ' +
                        (record.city || '').replace(/市$/, '')
                    }
                    sortable={false}
                />
                <FunctionField
                    label="设备状态"
                    source="deviceStatus"
                    render={record => record.deviceStatus == 1 ? '在线' : '离线'}
                    sortable={false}
                />
                <TextField source="softwareVersion" label="软件版本" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="firmwareVersion" label="固件版本" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                {/* <FunctionField
                    label="是否开启流量"
                    render={(record) => {
                        return <Box>{record.isFlow ? '是' : '否'}</Box>;
                    }}
                    sortable={false}
                /> */}
                <FunctionField
                    label="操作"
                    render={(record) => (
                        <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                            <OperationButton record={record} />
                            <ShowButton
                                label="详情"
                                record={{ id: record.tbDeviceId }}
                            />
                        </Box>
                    )}
                />
            </Datagrid>
        </List>
    );
};